import { FlexBox, ItemSelector } from '@airship-platform/jam-web/components';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';
import z from 'zod';
import { ManageableProduct } from '../../data-access';

const priceEditingPermissions: Record<
  ManageableProduct['type'],
  Set<string>
> = {
  MATERIAL: new Set(['canEditMaterialPrice', 'canEditMaterialCharge']),
  SERVICE: new Set(['canEditServicePrice']),
  EQUIPMENT: new Set(['canEditEquipmentPrice']),
};

export const canEditPrice = (
  claims: null | { userRole?: string; userPermissions: string[] },
  product: Pick<ManageableProduct, 'type'>
) =>
  !!claims &&
  (['AIRSHIP_ADMIN', 'OWNER'].includes(claims.userRole ?? '') ||
    claims.userPermissions.some((perm) =>
      priceEditingPermissions[product.type].has(perm)
    ));

const ProductPriceTypeSchema = z.enum([
  'COST',
  'STANDARD',
  'MEMBER',
  'STANDARD_ADDON',
  'MEMBER_ADDON',
  'DYNAMIC',
  'MANUAL',
]);

export const ProductPriceFormSchema = z.object({
  priceType: ProductPriceTypeSchema,
  value: z.number({ error: 'Price must be a number' }).finite({
    error: 'Price must be a number',
  }),
  currency: z.literal('USD'),
  taxable: z.boolean(),
});

export type ProductPriceFormValues = z.infer<typeof ProductPriceFormSchema>;

interface EditProductPriceFormProps {
  prices: ProductPriceFormValues[];
  isDisabled?: boolean;
  hasMembership: boolean;
}

const EditProductPriceForm: React.FC<EditProductPriceFormProps> = ({
  prices,
  isDisabled,
  hasMembership,
}) => {
  const { control, setValue, watch } = useFormContext<ProductPriceFormValues>();

  const visiblePrices = hasMembership
    ? prices.filter((price) => price.value > 0)
    : prices.filter(
        (price) => price.value > 0 && !price.priceType.startsWith('MEMBER')
      );

  const filteredPrices = visiblePrices.filter((price) =>
    ['STANDARD', 'MEMBER'].includes(price.priceType)
  );

  const addonPrices = visiblePrices.filter((price) =>
    price.priceType.endsWith(`_ADDON`)
  );

  const availablePrices = [...filteredPrices, ...addonPrices];

  const currentPriceType = watch('priceType');
  const hasAddOnAvailable = addonPrices.some((price) =>
    price.priceType.startsWith(currentPriceType)
  );

  return (
    <FlexBox flexDirection={'column'} gap={4} width={'100%'}>
      <Controller
        name="value"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label="Price"
            type="number"
            fullWidth
            disabled={isDisabled}
            error={!!error}
            helperText={error?.message}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (Number.isFinite(value)) {
                field.onChange(value);
                const match = availablePrices.find(
                  (price) => price.value === value
                );
                if (match) {
                  setValue('priceType', match.priceType);
                  setValue('currency', match.currency);
                  setValue('taxable', match.taxable);
                } else {
                  setValue('priceType', 'MANUAL');
                }
              } else {
                field.onChange('');
              }
            }}
          />
        )}
      />

      {!!filteredPrices.length && (
        <FlexBox flexDirection={'column'} gap={2}>
          <Typography variant="subtitle2">Select price</Typography>

          <Controller
            name="priceType"
            control={control}
            render={({ field }) => (
              <FlexBox gap={2}>
                {filteredPrices.map((price) => (
                  <ItemSelector
                    key={price.priceType}
                    label={
                      price.priceType.charAt(0).toUpperCase() +
                      price.priceType.slice(1).toLowerCase()
                    }
                    value={price.priceType}
                    checked={
                      field.value === price.priceType ||
                      field.value === `${price.priceType}_ADDON`
                    }
                    type={'radio'}
                    disabled={isDisabled}
                    handleChange={() => {
                      field.onChange(price.priceType);
                      setValue('value', price.value);
                      setValue('currency', price.currency);
                      setValue('taxable', price.taxable);
                    }}
                  />
                ))}
              </FlexBox>
            )}
          />
        </FlexBox>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={currentPriceType.endsWith('_ADDON')}
            color="tertiary"
            disabled={isDisabled || !addonPrices.length || !hasAddOnAvailable}
            onChange={() => {
              if (currentPriceType.endsWith('_ADDON')) {
                const match = prices.find(
                  (price) =>
                    price.priceType === currentPriceType.replace('_ADDON', '')
                );
                if (match) {
                  setValue('priceType', match.priceType);
                  setValue('value', match.value);
                  setValue('currency', match.currency);
                  setValue('taxable', match.taxable);
                }
              } else {
                const match = addonPrices.find((price) =>
                  price.priceType.startsWith(currentPriceType)
                );

                if (match) {
                  setValue('value', match.value);
                  setValue('currency', match.currency);
                  setValue('taxable', match.taxable);
                  setValue('priceType', match.priceType);
                }
              }
            }}
          />
        }
        label={
          !addonPrices.length
            ? 'Add-on (no add-on price available)'
            : !hasAddOnAvailable
              ? 'Add-on (not available for custom price)'
              : 'Add-on'
        }
      />
    </FlexBox>
  );
};

export default EditProductPriceForm;
